import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import Button from "../../../../../shared/ui/button/Button";
import { SessionTemplate, getTemplateName } from "../../../../session_templates";
import { SessionAnalysis, SessionNoteHighlight } from "../../../domain/Session";
import { useTherapist } from "../../../../../context/useTherapist";
import { listSessionHighlights, createSessionHighlight, deleteSessionHighlight } from "../../../application/sessionHighlights";
import { notesToHtml, stripStorageUris } from "../../utils/notesUtils";
import { Camera, Highlighter, Mic, PenLine, Upload, X } from "lucide-react";

interface SessionViewNotesProps {
    notes: string;
    onEdit: () => void;
    template?: SessionTemplate | null;
    analysis?: SessionAnalysis;
    sessionId?: string;
    patientId?: string;
    isPremium?: boolean;
    highlights?: SessionNoteHighlight[];
    onHighlightCreate?: (h: SessionNoteHighlight) => void;
    onHighlightDelete?: (id: string) => void;
    /**
     * A session with no notes yet — one opened ahead to be prepared — can
     * have them made here: recorded live, from an audio file or a photo of
     * handwritten notes. Given, those ways are offered.
     */
    onAddNotes?: (mode: NotesSource) => void;
    /** Recording, audio and photos go through the AI; without it, only writing. */
    aiAvailable?: boolean;
}

export type NotesSource = "live" | "upload_audio" | "image_notes";

const NOTES_SOURCES: { mode: NotesSource; icon: typeof Mic }[] = [
    { mode: "live", icon: Mic },
    { mode: "upload_audio", icon: Upload },
    { mode: "image_notes", icon: Camera },
];

const SOURCE_BUTTON =
    "flex min-w-[5.5rem] flex-1 flex-col items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-3 text-xs font-medium text-content-secondary transition-colors hover:border-brand-300 hover:bg-brand-50/50 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-700 dark:hover:bg-brand-500/10 sm:flex-none";


function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/** How a text node's content reads in serialized (sanitized) HTML. */
function escapeText(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function injectHighlights(html: string, highlights: SessionNoteHighlight[]): string {
    let result = html;
    for (const h of highlights) {
        if (!h.text.trim()) continue;
        const text = escapeText(h.text);
        const wrapped = `<span class="note-highlight" data-highlight-id="${escapeHtml(String(h.id))}">${text}</span>`;
        result = result.replace(text, () => wrapped);
    }
    return result;
}

/** Notes are user content (and legacy values are raw HTML): sanitize before rendering. */
function sanitizeNotesHtml(html: string): string {
    return DOMPurify.sanitize(html, { ADD_ATTR: ["target", "data-field-id"] });
}

export default function SessionViewNotes({ notes, onEdit, template, sessionId, patientId, isPremium, highlights: highlightsProp, onHighlightCreate, onHighlightDelete, onAddNotes, aiAvailable = false }: SessionViewNotesProps) {
    const { t } = useTranslation();
    const { therapist } = useTherapist();

    const [internalHighlights, setInternalHighlights] = useState<SessionNoteHighlight[]>([]);
    const controlled = highlightsProp !== undefined;
    const highlights = controlled ? highlightsProp : internalHighlights;

    const [selection, setSelection] = useState<{ text: string; top: number; left: number } | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleteError, setDeleteError] = useState(false);
    const selectionRef = useRef<{ text: string; top: number; left: number } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (controlled || !sessionId) return;
        listSessionHighlights(sessionId).then(data => setInternalHighlights(data)).catch(() => {});
    }, [sessionId, controlled]);

    const processedHtml = useMemo(() => {
        const html = sanitizeNotesHtml(notesToHtml(stripStorageUris(notes || '')));
        return injectHighlights(html, highlights);
    }, [notes, highlights]);

    const readSelection = useCallback(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) { setSelection(null); selectionRef.current = null; return; }
        const text = sel.toString().trim();
        if (!text) { setSelection(null); selectionRef.current = null; return; }
        const range = sel.getRangeAt(0);
        if (!containerRef.current?.contains(range.commonAncestorContainer)) {
            setSelection(null); selectionRef.current = null; return;
        }
        const rect = range.getBoundingClientRect();
        const containerRect = containerRef.current!.getBoundingClientRect();
        const info = {
            text,
            top: rect.top - containerRect.top - 48,
            left: rect.left - containerRect.left + rect.width / 2,
        };
        selectionRef.current = info;
        setSelection(info);
    }, []);

    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('[data-highlight-toolbar]')) return;
        readSelection();
    }, [readSelection]);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        // Don't clear selectionRef when tapping the toolbar — handleHighlight reads from it
        if ((e.target as HTMLElement).closest('[data-highlight-toolbar]')) return;
        // Delay so browser can finalize selection after touch lift
        setTimeout(readSelection, 100);
    }, [readSelection]);

    const handleHighlight = async () => {
        // Read from ref — immune to state race on mouseup
        const current = selectionRef.current;
        if (!current || !sessionId || !patientId || saving) return;
        setSaving(true);
        try {
            const highlight = await createSessionHighlight(sessionId, { text: current.text, patient_id: patientId });
            if (controlled) {
                onHighlightCreate?.(highlight);
            } else {
                setInternalHighlights(prev => [...prev, highlight]);
            }
            window.getSelection()?.removeAllRanges();
            setSelection(null);
            selectionRef.current = null;
        } catch {
            // ignore
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteHighlight = async (highlightId: string) => {
        try {
            await deleteSessionHighlight(highlightId);
            if (controlled) {
                onHighlightDelete?.(highlightId);
            } else {
                setInternalHighlights(prev => prev.filter(h => h.id !== highlightId));
            }
        } catch {
            setDeleteError(true);
            setTimeout(() => setDeleteError(false), 3000);
        }
    };

    const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest('[data-highlight-toolbar]')) return;
        const target = e.target as HTMLElement;
        const span = target.closest('[data-highlight-id]') as HTMLElement | null;
        if (span) {
            const id = span.getAttribute('data-highlight-id');
            if (id) handleDeleteHighlight(id);
        }
    };

    return (
        <div className="glass-effect glass-border border rounded-2xl p-8 shadow-theme-sm transition-all hover:shadow-theme-md">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    {t("session.sessionNotes")}
                </h3>
                <Button size="sm" variant="outline" className="text-content-tertiary hover:text-brand-500 hover:bg-brand-50" onClick={onEdit}>
                    {t("common.edit")}
                </Button>
            </div>

            <div
                className="notion-viewer min-h-[80px] relative"
                ref={containerRef}
                onMouseUp={handleMouseUp}
                onTouchEnd={handleTouchEnd}
                onClick={handleContainerClick}
            >
                {template && (
                    <div className="flex items-center gap-2 mb-5 px-1 bg-gray-50/50 dark:bg-white/5 w-fit pr-3 py-1 rounded-full border border-gray-100 dark:border-white/5">
                        <span className="text-xs font-bold text-brand-600/70 dark:text-brand-400/70 uppercase tracking-widest pl-2">
                            {t("templates.templateLabel", "Template")}:
                        </span>
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                            {getTemplateName(template, t, therapist?.language)}
                        </span>
                    </div>
                )}

                {/* Floating highlight toolbar */}
                {selection && sessionId && patientId && isPremium && (
                    <div
                        data-highlight-toolbar
                        className="absolute z-20 flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-accent/20 dark:border-purple-700 rounded-xl shadow-lg px-3 py-2 -translate-x-1/2"
                        style={{ top: `${selection.top}px`, left: `${selection.left}px` }}
                        onMouseDown={e => e.preventDefault()}
                        onMouseUp={e => e.stopPropagation()}
                        onTouchStart={e => e.stopPropagation()}
                    >
                        <button
                            onClick={handleHighlight}
                            disabled={saving}
                            className="flex items-center gap-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 disabled:opacity-50 transition-colors"
                        >
                            <Highlighter className="w-3.5 h-3.5" />
                            {t("session.highlight", "Highlight")}
                        </button>
                        <button
                            onClick={() => { window.getSelection()?.removeAllRanges(); setSelection(null); selectionRef.current = null; }}
                            className="text-content-tertiary hover:text-gray-600 dark:hover:text-gray-200 ml-1"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}

                {!notes && onAddNotes ? (
                    <div role="group" aria-label={t("session.addNotes.title")} className="space-y-3">
                        <p className="text-sm text-content-tertiary">{t("session.addNotes.title")}</p>
                        <div className="flex flex-wrap gap-2">
                            {aiAvailable &&
                                NOTES_SOURCES.map(({ mode, icon: Icon }) => (
                                    <button key={mode} type="button" className={SOURCE_BUTTON} onClick={() => onAddNotes(mode)}>
                                        <Icon className="h-5 w-5" aria-hidden="true" />
                                        {t(`session.addNotes.${mode}`)}
                                    </button>
                                ))}
                            <button type="button" className={SOURCE_BUTTON} onClick={onEdit}>
                                <PenLine className="h-5 w-5" aria-hidden="true" />
                                {t("session.addNotes.write")}
                            </button>
                        </div>
                    </div>
                ) : !notes ? (
                    <p className="text-content-tertiary italic text-sm">{t("session.notesHint")}</p>
                ) : (
                    <div
                        className="notion-viewer-content"
                        dangerouslySetInnerHTML={{ __html: processedHtml }}
                    />
                )}
            </div>

            {highlights.length > 0 && (
                <p className="mt-3 text-xs text-content-tertiary">
                    {t("session.highlightClickToRemove", "Click a highlight to remove it")}
                </p>
            )}
            {deleteError && (
                <p className="mt-2 text-xs text-red-500 dark:text-red-400">
                    {t("session.highlightDeleteError", "Failed to remove highlight. Please try again.")}
                </p>
            )}
        </div>
    );
}
